import { AccountRisksSettings, getScale, normalizeRiskAttributes } from "@/redux/account.types"
import { PhysicalRisk, PhysicalRiskAttribute, PhysicalRiskScaleItem } from "./physical-risk.types"

/**
 * Updates risks evaluation (frequency, prevention...) from the scale
 */
export function updateEvaluation(value: number | string, scale: PhysicalRiskScaleItem[]) {
  const num = Number(value)
  if (!Number.isFinite(num)) {
    return { value: 0, label: "" }
  }

  if (!Array.isArray(scale) || !scale.length) {
    return { value: num, label: "" }
  }

  const sorted = [...scale].sort((a, b) => a.value - b.value)

  // On prend le dernier seuil <= num, ou le premier niveau si la valeur est sous l'echelle
  const match = [...sorted].reverse().find((item) => num >= item.value) ?? sorted[0]

  //On renvoie la valeur du niveau, pas la valeur brute : une echelle modifiee apres coup laisse des
  //risques hors seuils (frequence a 1 sur une echelle qui demarre a 2). Sans ce recalage, l'echelle
  //n'affiche aucune ligne selectionnee et le score se calcule sur une valeur que le libelle contredit.
  return {
    value: match.value,
    label: match.label
  }
}

//Prevention d'un nouveau risque : le plus bas niveau du barème (aucune mesure supposee)
export function getLowestPreventionValue(settings: Pick<AccountRisksSettings, "preventionScale">) {
  const scale = getScale(settings.preventionScale)
  return scale.length ? Math.min(...scale.map((item) => item.value)) : 1
}

/**
 * Evaluation d'une fonction du risque : sa gravite et sa frequence propres, sinon celles du risque.
 * Meme regle cote serveur (backend-ts/src/utils/risk-evaluation.utils).
 */
export function getPopulationEvaluation(risk: PhysicalRisk, populationId: string) {
  const evaluation = risk.populationEvaluations?.find((item) => item.PopulationId === populationId)
  return {
    severity: evaluation?.severity ?? risk.severity,
    frequency: evaluation?.frequency ?? risk.frequency
  }
}

//Hors d'une fonction, le risque prend la plus elevee de ses fonctions ; la sienne s'il n'en a aucune
export function getMaxEvaluation(risk: PhysicalRisk) {
  const populationIds = risk.populationIds ?? []
  const evaluations = populationIds.length
    ? populationIds.map((id) => getPopulationEvaluation(risk, id))
    : [{ severity: risk.severity, frequency: risk.frequency }]
  return evaluations.reduce((max, evaluation) =>
    evaluation.severity * evaluation.frequency > max.severity * max.frequency ? evaluation : max
  )
}

/**
 * Attributs a afficher pour un risque. cAttributes vient du formatter, mais un document archive sans
 * snapshot de reglages laisse ses risques non formates (cf. risks-document.route) : on retombe alors
 * sur les cles brutes du JSON, faute de libelles connus.
 */
export function getRiskAttributeFields(risk: PhysicalRisk): PhysicalRiskAttribute[] {
  if (risk.cAttributes?.length) return risk.cAttributes

  return Object.entries(risk.attributes ?? {}).map(([id, value]) => ({ id, name: id, value: value ?? "" }))
}

/** Attributs indexes par libelle : format attendu par le generateur de PDF (service qr-code) */
export function toNamedRiskAttributes(risk: PhysicalRisk, riskAttributes: any): Record<string, string> {
  const attributes = risk.cAttributes?.length ? risk.cAttributes : getNamedFallback(risk, riskAttributes)

  return attributes.reduce<Record<string, string>>((named, attribute) => {
    named[attribute.name] = attribute.value
    return named
  }, {})
}

function getNamedFallback(risk: PhysicalRisk, riskAttributes: any): PhysicalRiskAttribute[] {
  const settingsAttributes = normalizeRiskAttributes(riskAttributes)
  if (!settingsAttributes.length) return getRiskAttributeFields(risk)

  return settingsAttributes.map((attribute) => ({ ...attribute, value: risk.attributes?.[attribute.id] ?? "" }))
}

function normalizeAttributeName(value: string) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
}

/**
 * Les assistants raisonnent en libelles : leurs reponses arrivent donc indexees par libelle, alors
 * que le risque se stocke par id. La correspondance se fait sur le libelle normalise, et a defaut
 * sur la position, l'ordre des attributs etant celui donne dans le prompt.
 */
export function mapNamedAttributesToIds(generated: any, riskAttributes: any): Record<string, string> {
  const settingsAttributes = normalizeRiskAttributes(riskAttributes)
  const generatedEntries = Object.entries(generated ?? {}).filter(([, value]) => typeof value === "string")
  const generatedByName = new Map(generatedEntries.map(([name, value]) => [normalizeAttributeName(name), value]))

  return settingsAttributes.reduce<Record<string, string>>((attributes, attribute, index) => {
    const value =
      generated?.[attribute.id] ??
      generated?.[attribute.name] ??
      generatedByName.get(normalizeAttributeName(attribute.name)) ??
      generatedEntries[index]?.[1] ??
      ""

    attributes[attribute.id] = typeof value === "string" ? value.trim() : ""

    return attributes
  }, {})
}

/** Affiche une valeur de risque sans decimale inutile : le risque residuel est une division */
export function formatRiskValue(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}
